import { Modal, useModals } from 'almanac-ui/Modal'
import { useState } from 'react'
import { PressableBrandAICard } from 'screens/BrandKits/components/BrandAICard/BrandAICard'
import { BrandStylePreview } from 'screens/BrandKits/components/BrandStandard/BrandStyleCard/BrandStylePreview'
import { BrandVisualKit } from 'types'
import Styles from '../DesignWizardField.module.scss'
import { BrandStyle } from 'blaze-ui/icons/24'
import { Radio, RadioGroup } from 'components/RadioGroup'
import { RadioBullet } from 'components/RadioGroup/RadioBullet'
import { Button } from 'blaze-ui/Button'
import { BrandWizardIntegrationSelection } from 'components/BrandWizardIntegrationSelection/BrandWizardIntegrationSelection'
import { Plus } from 'blaze-ui/icons'
import { NewBrandStyleCard } from 'screens/BrandKits/components/BrandStandard/NewBrandStyleCard/NewBrandStyleCard'

interface Props {
  brandVisualKits: BrandVisualKit[]
  onSelect: (brandVisualKitId?: number | null) => void
  initialKit?: BrandVisualKit
}

export const BrandKitsModal = ({ brandVisualKits, onSelect, initialKit }: Props) => {
  const [selectedKit, setSelectedKit] = useState<number | undefined>(initialKit?.id)
  const { openModal, closeModal, closeStack } = useModals()

  const handleSubmit = () => {
    onSelect(selectedKit || null)
    closeModal()
  }

  const onConfirm = (kit: BrandVisualKit) => {
    onSelect(kit.id)
    closeStack()
  }

  const handlePress = () => {
    openModal(
      BrandWizardIntegrationSelection,
      {
        onConfirm,
      },
      { newStack: true },
    )
  }

  return (
    <Modal.Root onPressOutside={closeModal}>
      <Modal.Header
        title={
          <div className={Styles.title}>
            <BrandStyle />
            Brand Style
          </div>
        }
        actions={
          <Button variant="tertiary" frontIcon={Plus} onPress={handlePress}>
            Create New
          </Button>
        }
      />
      <Modal.Content>
        <RadioGroup aria-label="Title" value={selectedKit?.toString()}>
          <div className={Styles.grid}>
            {brandVisualKits.map((brandVisualKit, i) => {
              return (
                <Radio className={Styles.radioOption} value={brandVisualKit.id.toString()} key={i} hideRadio>
                  {(radioBulletProps) => (
                    <div className={Styles.radioItem}>
                      {brandVisualKit.id === selectedKit && (
                        <div className={Styles.radioBullet}>
                          <RadioBullet {...radioBulletProps} size={16} />
                        </div>
                      )}
                      <PressableBrandAICard
                        key={brandVisualKit.id}
                        size="md"
                        withShadow
                        lineClamp={1}
                        preview={() => (
                          <BrandStylePreview kit={brandVisualKit} size="md" backgroundColor="var(--light-80)" />
                        )}
                        onPress={() =>
                          setSelectedKit(brandVisualKit.id === selectedKit ? undefined : brandVisualKit.id)
                        }
                        isSelected={selectedKit === brandVisualKit.id}
                        title={brandVisualKit.name}
                      />
                    </div>
                  )}
                </Radio>
              )
            })}
            {brandVisualKits.length % 3 !== 0 && (
              <NewBrandStyleCard size="md" title="Create New Brand Style" onConfirm={onConfirm} />
            )}
          </div>
        </RadioGroup>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton onPress={closeModal} variant="tertiary" size="lg">
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton onPress={handleSubmit} size="lg" variant="primary">
            {selectedKit ? 'Use Selected Style' : "Don't Use Style"}
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
